/**
 * Centralized utilities for user details mapping and display name computation
 * This eliminates the scattered display name logic across the codebase
 */

interface UserDetailsResponse {
    mobile?: string
    countryCode?: string
    username?: string
    screenName?: string
    travellerDetails?: Array<{
        personalDetails?: {
            firstName?: string
            lastName?: string
        }
    }>
}

interface UserDisplayInfo {
    mobile?: string
    countryCode?: string
    email?: string
    firstName?: string
    lastName?: string
    displayName: string
    fullName: string
}

/**
 * Computes display name with fallback priority:
 * 1. First name
 * 2. Mobile with country code (e.g., "+966 123456789")
 * 3. Mobile without country code
 * 4. Email
 * 5. Username
 * 6. 'User' as last resort
 */
export function computeDisplayName(params: {
    mobile?: string
    countryCode?: string
    email?: string
    username?: string
    firstName?: string
}): string {
    const { mobile, countryCode, email, username, firstName } = params

    // Priority 1: First name
    if (firstName) {
        return firstName
    }

    // Priority 2: Mobile with country code
    if (mobile && countryCode) {
        return `${countryCode} ${mobile}`
    }

    // Priority 3: Mobile without country code
    if (mobile) {
        return mobile
    }

    // Priority 4: Email
    if (email) {
        return email
    }

    // Priority 5: Username
    if (username) {
        return username
    }

    // Fallback
    return 'User'
}

/**
 * Computes full name with fallback priority:
 * 1. First name + Last name
 * 2. Display name
 */
export function computeFullName(params: { firstName?: string; lastName?: string; displayName: string }): string {
    const { firstName, lastName, displayName } = params

    if (firstName && lastName) {
        return `${firstName} ${lastName}`
    }

    return displayName
}

/**
 * Maps API user details response to normalized user display info
 * This is the single source of truth for user data transformation
 */
export function mapUserDetailsToDisplayInfo(
    userDetailsResponse: UserDetailsResponse | null | undefined,
    fallbackMobile?: string,
    fallbackCountryCode?: string,
    fallbackEmail?: string
): UserDisplayInfo {
    if (!userDetailsResponse) {
        // No API response - use fallback data
        const displayName = computeDisplayName({
            mobile: fallbackMobile,
            countryCode: fallbackCountryCode,
            email: fallbackEmail
        })

        return {
            mobile: fallbackMobile,
            countryCode: fallbackCountryCode,
            email: fallbackEmail,
            displayName,
            fullName: displayName
        }
    }

    // Extract data from API response
    const mobile = userDetailsResponse.mobile || fallbackMobile
    const countryCode = userDetailsResponse.countryCode || fallbackCountryCode
    const username = userDetailsResponse.username || userDetailsResponse.screenName
    const personalDetails = userDetailsResponse.travellerDetails?.[0]?.personalDetails
    const firstName = personalDetails?.firstName
    const lastName = personalDetails?.lastName
    const email = fallbackEmail || username

    // Compute display name
    const displayName = computeDisplayName({
        mobile,
        countryCode,
        email,
        username,
        firstName
    })

    // Compute full name
    const fullName = computeFullName({
        firstName,
        lastName,
        displayName
    })

    return {
        mobile,
        countryCode,
        email,
        firstName,
        lastName,
        displayName,
        fullName
    }
}

/**
 * Creates auth state object from user display info
 * Used for Redux setAuthUser calls
 */
export function createAuthStateFromUserInfo(userInfo: UserDisplayInfo) {
    return {
        signedIn: true,
        mobile: userInfo.mobile || undefined,
        countryCode: userInfo.countryCode || undefined,
        displayName: userInfo.displayName,
        firstName: userInfo.firstName || undefined,
        lastName: userInfo.lastName || undefined,
        fullName: userInfo.fullName,
        email: userInfo.email || undefined
    }
}

/**
 * Convenience function that combines fetching user details and creating auth state
 * Handles all error cases with proper fallbacks
 */
export async function fetchAndMapUserDetails(
    fetchUserDetails: () => Promise<any>,
    fallbackData: {
        mobile?: string
        countryCode?: string
        email?: string
    }
) {
    try {
        const userDetailsResponse = await fetchUserDetails()
        const userInfo = mapUserDetailsToDisplayInfo(
            userDetailsResponse,
            fallbackData.mobile,
            fallbackData.countryCode,
            fallbackData.email
        )
        return createAuthStateFromUserInfo(userInfo)
    } catch (error) {
        console.error('Failed to fetch user details:', error)
        // Return fallback auth state
        const userInfo = mapUserDetailsToDisplayInfo(
            null,
            fallbackData.mobile,
            fallbackData.countryCode,
            fallbackData.email
        )
        return createAuthStateFromUserInfo(userInfo)
    }
}
